import * as Vue from "vue";
var StorageType = /* @__PURE__ */ ((StorageType2) => {
  StorageType2["localStorage"] = "localStorage";
  StorageType2["sessionStorage"] = "sessionStorage";
  StorageType2["cookieStorage"] = "cookieStorage";
  return StorageType2;
})(StorageType || {});
function isArrayIntersecting(arr1, arr2) {
  for (let i = 0; i < arr1.length; i++) {
    const role = arr1[i];
    if (arr2.indexOf(role) >= 0) {
      return true;
    }
  }
  return false;
}
function getVueVersion(app) {
  var _a;
  return Number((_a = app.version) == null ? void 0 : _a.split(".")[0]);
}
function isStorageAvailable(storage) {
  if (storage === StorageType.cookieStorage) {
    return true;
  }
  const storageValue = window[storage];
  if (!storageValue || typeof storageValue !== "object" || typeof storageValue.setItem !== "function" || typeof storageValue.removeItem !== "function") {
    return false;
  }
  try {
    window[storage].setItem("vue-route-guard", "vue-route-guard");
    window[storage].removeItem("vue-route-guard");
    return true;
  } catch (e) {
    return false;
  }
}
class CookieStorage {
  setItem(key, value, attributes = {}) {
    attributes.expires = this.getDate((attributes == null ? void 0 : attributes.expires) || "");
    this.setCookie(key, value, attributes);
  }
  getItem(name) {
    if (typeof document === "undefined" || !name) {
      return null;
    }
    const cookie = (document.cookie.replace(/;\s+/g, ";").split(";") || []).map((s) => {
      return s.replace(/\s+=\s+/g, "=").split("=");
    });
    const keyIndex = 0;
    const valueIndex = 1;
    for (let i = 0; i < cookie.length; i++) {
      if (cookie[i][keyIndex] && cookie[i][keyIndex] === name) {
        return cookie[i][valueIndex];
      }
    }
    return null;
  }
  removeItem(name) {
    this.setCookie(name, "", {
      expires: -1
    });
  }
  setCookie(name, value, attributes = {}) {
    if (typeof document === "undefined" || !name) {
      return;
    }
    const formattedValue = encodeURIComponent(value).replace(/%(2[346BF]|3[AC-F]|40|5[BDE]|60|7[BCD])/g, decodeURIComponent);
    let cookie = `${name}=${formattedValue};`;
    for (const attributeName in attributes) {
      const attributeValue = attributes[attributeName];
      if (attributeValue === false || attributeValue === void 0) {
        continue;
      }
      if (attributeValue === true) {
        cookie += ` ${attributeName};`;
      } else {
        cookie += ` ${attributeName}=${attributeValue};`;
      }
    }
    document.cookie = cookie;
  }
  getDate(val) {
    if (typeof val === "number") {
      const milisecondsDay = 864e5;
      return new Date(Date.now() + val * milisecondsDay).toUTCString();
    }
    return val;
  }
}
class GuardStorage {
  constructor(storageType = StorageType.sessionStorage) {
    if (!this.checkStorage(storageType)) {
      throw Error(`@thisdot/vue-route-guard: ${storageType} is not supported`);
    }
    const storageList = {
      [StorageType.sessionStorage]: window.sessionStorage,
      [StorageType.localStorage]: window.localStorage,
      [StorageType.cookieStorage]: new CookieStorage()
    };
    this.storage = storageList[storageType];
  }
  set(key, value, attributes) {
    this.storage.setItem(key, value, attributes);
  }
  get(key) {
    return this.storage.getItem(key);
  }
  remove(key) {
    this.storage.removeItem(key);
  }
  checkStorage(name) {
    var _a;
    if (!((_a = Object.values(StorageType)) == null ? void 0 : _a.includes(name))) {
      throw new Error("@thisdot/vue-route-guard: Storage is not supported");
    }
    return isStorageAvailable(name);
  }
}
const defaultOptions = {
  permissionKey: "permission"
};
let guardStore;
class Guard {
  constructor(config, packageKey2) {
    var _a;
    if (!((_a = config.token) == null ? void 0 : _a.name)) {
      throw Error("@thisdot/vue-route-guard: Token name is not set");
    }
    Guard.router = config.router;
    Guard.tokenConfig = config.token;
    Guard.redirect = config.redirect || {};
    Guard.options = Object.assign({}, defaultOptions, config.options);
    Guard.storage = new GuardStorage(config.token.storage);
    Guard.packageKey = packageKey2;
    guardStore = Vue.reactive({
      state: {
        loading: false,
        authentication: null,
        isAuthenticated: false
      }
    });
    this.$store = guardStore;
    Guard.initializeAuthentication();
    Guard.router.beforeEach((guard) => {
      if (guard.meta.requiresAuth && !guardStore.state.isAuthenticated) {
        return Guard.redirect.noAuthentication || false;
      }
      if (guard.meta.requiresAuth && !this.hasAuthenticationAccess(guard.meta.access)) {
        return Guard.redirect.noPermission || Guard.redirect.noAuthentication || false;
      }
      return true;
    });
  }
  install(app) {
    app.config.globalProperties[`$${Guard.packageKey}`] = this;
    app.provide(Guard.packageKey, this);
  }
  token() {
    return Guard.storage.get(Guard.tokenConfig.name);
  }
  isAuthenticated() {
    return guardStore.state.isAuthenticated;
  }
  hasAuthenticationAccess(permission = []) {
    const permissionKey = Guard.options.permissionKey;
    if (guardStore.state.isAuthenticated === true && (permission.length === 0 || permission.length > 0 && permissionKey && guardStore.state.authentication && guardStore.state.authentication[permissionKey] && isArrayIntersecting(permission, guardStore.state.authentication[permissionKey]))) {
      return true;
    }
    return false;
  }
  async clearAuthentication() {
    Guard.clearAuthenticationState();
    Guard.clearToken();
    const redirectRoute = Guard.redirect.clearAuthentication || Guard.redirect.noAuthentication;
    if (redirectRoute) {
      Guard.router.push(redirectRoute);
    }
    return true;
  }
  async refreshAuthentication() {
    await Guard.initializeAuthentication();
    return true;
  }
  async setToken({ token, attributes }) {
    const tokenAttributes = Object.assign({}, Guard.tokenConfig.attributes, attributes);
    Guard.storage.set(Guard.tokenConfig.name, token, tokenAttributes);
    await Guard.initializeAuthentication();
    return true;
  }
  static isTokenAvailable() {
    return !!Guard.storage.get(Guard.tokenConfig.name);
  }
  static clearToken() {
    Guard.storage.remove(Guard.tokenConfig.name);
  }
  static setAuthenticationState(authentication) {
    guardStore.state.loading = false;
    guardStore.state.authentication = authentication;
    guardStore.state.isAuthenticated = true;
  }
  static clearAuthenticationState() {
    guardStore.state.loading = false;
    guardStore.state.authentication = null;
    guardStore.state.isAuthenticated = false;
  }
  static async initializeAuthentication() {
    if (Guard.isTokenAvailable() && typeof Guard.options.fetchAuthentication === "function") {
      guardStore.state.loading = true;
      const auntenticationData = await Guard.options.fetchAuthentication();
      Guard.setAuthenticationState(auntenticationData);
    }
    return true;
  }
}
const packageKey = "guard";
function setupGuard(options) {
  if (getVueVersion(Vue) <= 2) {
    throw new Error("@thisdot/vue-route-guard: Vue 2 is not supported");
  }
  return new Guard(options, packageKey);
}
function useGuard() {
  return Vue.inject(packageKey);
}
export { StorageType, setupGuard, useGuard };
